import { Component, OnInit } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AccountInfo, InteractionStatus } from '@azure/msal-browser';
import { filter } from 'rxjs/operators';
import { NgxSpinnerService } from 'ngx-spinner';
import { CodeCoverageService } from './codecoverage.service';
import { ICodeCoverageResult } from '../shared/codeCoverage';

@Component({
  selector: 'app-codecoverage',
  templateUrl: './codecoverage.component.html',
  styleUrls: ['./codecoverage.component.css']
})
export class CodeCoverageComponent implements OnInit {

  public results: ICodeCoverageResult[] = [];
  public filtered: ICodeCoverageResult[] = [];
  public error: string = '';
  public loaded: boolean = false;
  public loginDisplay: boolean = false;
  public message: string = 'You must login to view this page.';
  public searchTerm: string = '';
  public sortField: string = 'lineCoveragePercent';
  public sortAsc: boolean = true;
  public selectedResult: ICodeCoverageResult | null = null;
  public refreshing: boolean = false;

  constructor(
    private service: CodeCoverageService,
    private spinner: NgxSpinnerService,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService
  ) { }

  ngOnInit() {
    this.handleSecurity();
  }

  handleSecurity() {
    this.authService.handleRedirectObservable().subscribe({
      next: (result) => {
        if (result) {
          this.authService.instance.setActiveAccount(result.account);
        }
        this.setLoginDisplay();
      },
      error: (_err) => { }
    });

    this.msalBroadcastService.inProgress$
      .pipe(filter((status: InteractionStatus) => status === InteractionStatus.None))
      .subscribe(() => {
        this.setLoginDisplay();
      });
  }

  setLoginDisplay() {
    const accounts: AccountInfo[] = this.authService.instance.getAllAccounts();
    this.loginDisplay = accounts.length > 0;
    if (this.loginDisplay && !this.loaded) {
      this.load();
    }
  }

  load() {
    this.spinner.show();
    this.service.getAll().subscribe({
      next: (data) => {
        this.results = data;
        this.applyFilter();
        this.loaded = true;
        this.spinner.hide();
      },
      error: (_err) => {
        this.error = 'Failed to load code coverage data.';
        this.loaded = true;
        this.spinner.hide();
      }
    });
  }

  applyFilter() {
    const term = this.searchTerm.toLowerCase();
    let data = term
      ? this.results.filter(r =>
          r.project.toLowerCase().includes(term) ||
          r.pipeline.toLowerCase().includes(term))
      : [...this.results];

    data.sort((a, b) => {
      const av = a[this.sortField];
      const bv = b[this.sortField];
      if (typeof av === 'string') {
        return this.sortAsc ? av.localeCompare(bv) : (bv as string).localeCompare(av);
      }
      return this.sortAsc ? (av as number) - (bv as number) : (bv as number) - (av as number);
    });

    this.filtered = data;
  }

  sortBy(field: string) {
    if (this.sortField === field) {
      this.sortAsc = !this.sortAsc;
    } else {
      this.sortField = field;
      this.sortAsc = field === 'lineCoveragePercent' ? true : true;
    }
    this.applyFilter();
  }

  sortIcon(field: string): string {
    if (this.sortField !== field) return '↕';
    return this.sortAsc ? '↑' : '↓';
  }

  coverageClass(pct: number): string {
    if (pct >= 70) return 'cov-high';
    if (pct >= 40) return 'cov-medium';
    return 'cov-low';
  }

  get averageCoverage(): number {
    if (!this.filtered.length) return 0;
    return this.filtered.reduce((sum, r) => sum + r.lineCoveragePercent, 0) / this.filtered.length;
  }

  get highCount(): number { return this.filtered.filter(r => r.lineCoveragePercent >= 70).length; }
  get mediumCount(): number { return this.filtered.filter(r => r.lineCoveragePercent >= 40 && r.lineCoveragePercent < 70).length; }
  get lowCount(): number { return this.filtered.filter(r => r.lineCoveragePercent < 40).length; }

  openDetail(r: ICodeCoverageResult) {
    this.selectedResult = r;
  }

  closeDetail() {
    this.selectedResult = null;
  }

  forceRefresh() {
    this.refreshing = true;
    this.service.refresh().subscribe({
      next: () => {
        this.refreshing = false;
        this.load();
      },
      error: () => {
        this.refreshing = false;
      }
    });
  }

  buildUrl(r: ICodeCoverageResult): string {
    return `https://dev.azure.com/fsrdev/${encodeURIComponent(r.project)}/_build/results?buildId=${r.buildId}&view=results`;
  }

  ringDashArray(pct: number): string {
    const circumference = 2 * Math.PI * 40;
    const filled = (pct / 100) * circumference;
    return `${filled} ${circumference}`;
  }
}
